import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { StatusBarStyle } from 'expo-status-bar';
import { color } from '@/constants/tokens';

/**
 * ── Safe-area inset color: global default + per-screen override ──────────
 *
 * Every screen renders a NATIVE stack header (headers are ON by default on the
 * root Stack), and a native header draws its OWN top-notch inset. So the root
 * `SafeAreaView` in app/_layout.tsx takes NO top edge (`edges={[]}`) — the
 * header owns the notch on every screen (a uniform inset is what stops
 * navigation from shifting the layout). The root SafeAreaView survives only to
 * PAINT the strip background color behind the notch: that color shows through
 * on screens whose header is TRANSPARENT (e.g. the event page's dark hero); an
 * opaque header paints its own notch region and simply doesn't rely on it.
 *
 * This module owns that strip color. Default is `'light'` (near-white strip,
 * dark glyphs). A screen with a transparent header over a dark hero calls
 * `useInsetStyle('dark')` for the duration it's mounted, reverting on unmount,
 * so the notch behind the floating header reads `brandDark` instead of white.
 *
 * A plain (opaque-header) screen needs to know NOTHING about this: it inherits
 * the default, and its own header hides the strip anyway. Only a transparent-
 * header screen opts in.
 *
 * Deliberately just TWO variants — `'light'` and `'dark'`. The caller picks the
 * strip, and the matching status-bar glyph style is DERIVED here, not passed in:
 * that makes "dark glyphs on a dark strip" (invisible time/battery) unrepresent-
 * able. Add a third variant here, not at the call site, if a real screen needs
 * one.
 *
 * Note on Android 15+: edge-to-edge is enforced and the native status-bar
 * background color is deprecated/ignored, so coloring the strip via this
 * SafeAreaView background is the only correct approach — `expo-status-bar` is
 * used for glyph `style` only, never background.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** The two strip variants a screen may choose. `light` is the app default. */
export type InsetVariant = 'light' | 'dark';

/**
 * The fixed color-token + glyph-style pairing for each variant. Paired here so
 * a variant can't be split into a mismatched color/glyph combination anywhere.
 */
const VARIANTS: Record<
  InsetVariant,
  { color: keyof typeof color; statusBarStyle: StatusBarStyle }
> = {
  // These `color:` values are token NAMES (keys of the `color` group), resolved
  // to a raw hex/hsla in useResolvedInsetStyle. Do NOT call getTokenValue here:
  // at module-eval time Tamagui's token registry isn't populated, so it returns
  // a wrong value (the white strip we saw). Reading `color[name]` is exact.
  // no-hardcoded-tokens can't tell a token NAME used as a lookup key from a
  // hardcoded color prop, so it false-positives on these two lines. Suppress
  // rather than obfuscate — the names are validated by `keyof typeof color`.
  // Light strip → dark glyphs. The near-white app background.
  // eslint-disable-next-line local-rules/no-hardcoded-tokens
  light: { color: 'background', statusBarStyle: 'dark' },
  // Near-black strip → light glyphs. For dark heroes that bleed into the notch.
  // eslint-disable-next-line local-rules/no-hardcoded-tokens
  dark: { color: 'brandDark', statusBarStyle: 'light' },
};

const DEFAULT_VARIANT: InsetVariant = 'light';

type InsetVariantContextValue = {
  variant: InsetVariant;
  setVariant: (variant: InsetVariant | null) => void;
};

const InsetVariantContext = createContext<InsetVariantContextValue | null>(
  null
);

/**
 * Owns the current inset variant. Mount ABOVE the root `SafeAreaView` so the
 * SafeAreaView + StatusBar can read from it. Defaults to `'light'` until a
 * screen overrides.
 */
export function InsetStyleProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<InsetVariant | null>(null);

  const value = useMemo<InsetVariantContextValue>(
    () => ({ variant: override ?? DEFAULT_VARIANT, setVariant: setOverride }),
    [override]
  );

  return (
    <InsetVariantContext.Provider value={value}>
      {children}
    </InsetVariantContext.Provider>
  );
}

function useInsetVariantContext(): InsetVariantContextValue {
  const ctx = useContext(InsetVariantContext);
  if (!ctx) {
    throw new Error(
      'Inset-style hooks must be used inside <InsetStyleProvider> (mounted in app/_layout.tsx).'
    );
  }
  return ctx;
}

/**
 * Read the resolved strip color + status-bar style for the current variant.
 * For the root SafeAreaView / StatusBar to consume — screens use
 * `useInsetStyle` instead.
 */
export function useResolvedInsetStyle(): {
  backgroundColor: string;
  statusBarStyle: StatusBarStyle;
} {
  const { variant } = useInsetVariantContext();
  const { color: token, statusBarStyle } = VARIANTS[variant];
  return {
    // Read the raw hex/hsla straight from the token object — NOT via
    // getTokenValue(`$${token}`). getTokenValue depends on Tamagui's runtime
    // token registry (`specificTokens`), which isn't reliably populated for a
    // dynamically-built token string here; it silently returned a wrong value
    // (white strip instead of #15141B). `color` is the same source of truth
    // tamagui.config feeds into createTokens, so this is exact and deterministic.
    backgroundColor: color[token],
    statusBarStyle,
  };
}

/**
 * Set the safe-area strip variant for as long as the calling screen is mounted;
 * reverts to the app default (`'light'`) on unmount. The status-bar glyph style
 * follows the variant automatically.
 *
 * @example
 *   // A dark hero that bleeds into the notch:
 *   useInsetStyle('dark');
 */
export function useInsetStyle(variant: InsetVariant): void {
  const { setVariant } = useInsetVariantContext();

  useEffect(() => {
    setVariant(variant);
    return () => setVariant(null);
  }, [variant, setVariant]);
}
